import { INFRASTRUCTURE_FALLBACK, INFRASTRUCTURE_TARGETS } from "../combat-types.js";
import type { StandingOrder } from "../logistics-types.js";
import type { ConflictIntent, SeatObservation } from "../types.js";
import { seatName } from "./border-situation.js";
import { help } from "./layout-view.js";
import { type AppNode, gameIntent, name, text } from "./view-nodes.js";

const descriptions: Record<StandingOrder, string> = {
  hold: "Return fire. Protect this front; do not attack buildings.",
  assault: "Fight troops first, then the chosen infrastructure target.",
  raid: "Raiders split fire into infrastructure after shields fall. No Metal is stolen.",
};

export function borderOrders(o: SeatObservation, id: string): AppNode {
  const current = o.self.logistics.borders[id]?.standingOrder ?? "hold";
  const target = o.self.combat.borders[id]?.requestedTarget ?? "headquarters";
  return {
    kind: "fieldset",
    id: `orders-${id}`,
    legend: `Your standing order · ${seatName(o, id)}`,
    children: [
      {
        kind: "row",
        id: `order-buttons-${id}`,
        style: { className: "kf-order-buttons" },
        children: (["hold", "assault", "raid"] as const).map((order) =>
          choice(
            `order-${id}-${order}`,
            name(order),
            current === order,
            { kind: "set-standing-order", borderId: id, order },
            descriptions[order],
          ),
        ),
      },
      help(
        `raid-help-${id}`,
        "How Raid works",
        "Raid never steals Metal. With defenders present and their shield down at the start of a combat tick, Raider Robots use 20% of their fire against infrastructure and 80% against troops (35% / 65% with Raiding Force). Guards and Siege Guns have no special Raid benefit. With no defenders, Assault and Raid both attack infrastructure. Shields absorb incoming damage first.",
      ),
      {
        kind: "infobox",
        id: `targets-${id}`,
        title: `Infrastructure target · ${name(target)}`,
        initiallyOpen: false,
        content: [
          text(
            `target-help-${id}`,
            "Hold never attacks infrastructure. Assault and Raid use this target when their rules permit.",
          ),
          {
            kind: "row",
            id: `target-buttons-${id}`,
            style: { className: "kf-target-buttons" },
            children: INFRASTRUCTURE_TARGETS.map((next) =>
              choice(`target-${id}-${next}`, name(next), target === next, {
                kind: "set-infrastructure-target",
                borderId: id,
                target: next,
              }),
            ),
          },
          text(
            `effective-${id}`,
            `Effective target: ${name(o.self.combat.borders[id]?.effectiveTarget ?? target)}. Empty targets fall back through: ${INFRASTRUCTURE_FALLBACK.map(name).join(" → ")}.`,
          ),
        ],
      },
    ],
  };
}

function choice(
  id: string,
  label: string,
  selected: boolean,
  intent: ConflictIntent,
  description?: string,
): AppNode {
  return {
    kind: "section",
    id: `${id}-choice`,
    style: { className: selected ? "kf-choice kf-choice-selected" : "kf-choice" },
    children: [
      {
        kind: "action",
        id,
        action: {
          id,
          label: `${selected ? "✓ " : ""}${label}${selected ? " · selected" : ""}`,
          enabled: true,
          intent: gameIntent(id, intent),
          blockers: [],
          ...(description ? { tooltip: description } : {}),
        },
      },
    ],
  };
}
